import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  Breadcrumbs,
  Link,
  Chip,
  Skeleton,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  LinearProgress,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DownloadIcon from '@mui/icons-material/Download';
import { companiesAPI, personsAPI } from '../services/api';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import JurisdictionBadge from '../components/JurisdictionBadge/JurisdictionBadge';
import { getJurisdictionColor, lightenColor } from '../utils/jurisdictionColors';
import { toast } from 'react-toastify';
import CompanyEditDialog from '../components/CompanyEditDialog';
import OwnershipGraph from '../components/OwnershipGraph/OwnershipGraph';
import { Button } from '@mui/material';

const Regions: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [ownershipGraph, setOwnershipGraph] = useState<any | null>(null);
  const [directorsForSelected, setDirectorsForSelected] = useState<Array<{ personId: string; name: string; roleTitle?: string; isNominee?: boolean; profilePhotoUrl?: string }>>([]);
  const location = useLocation();
  const [ownersByCompanyId, setOwnersByCompanyId] = useState<Record<string, string>>({});
  const [ownersLoading, setOwnersLoading] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sortBy, setSortBy] = useState<'name' | 'owners' | 'missing'>('name');
  const [editOpen, setEditOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [enhanceJobId, setEnhanceJobId] = useState<string | null>(null);
  const [enhanceData, setEnhanceData] = useState<any | null>(null);
  const [applySelection, setApplySelection] = useState<Record<string, any>>({});

  const getFlagEmoji = (jurisdiction: string | null | undefined): string => {
    const j = String(jurisdiction || '').toLowerCase();
    if (j === 'malawi') return '🇲🇼';
    if (j === 'tanzania') return '🇹🇿';
    if (j === 'uganda') return '🇺🇬';
    if (j === 'kenya') return '🇰🇪';
    if (j === 'zambia' || j === 'zambia m') return '🇿🇲';
    if (j === 'mozambique') return '🇲🇿';
    if (j === 'drc' || j.includes('congo')) return '🇨🇩';
    if (j === 'united kingdom' || j === 'uk') return '🇬🇧';
    if (j === 'india') return '🇮🇳';
    if (j.includes('united arab emirates') || j === 'uae') return '🇦🇪';
    return '';
  };

  useEffect(() => {
    const load = async () => {
      try {
        // Backend expects { page, limit } (integers)
        const res = await companiesAPI.getAll({ page: 1, limit: 5000 });
        const data = res.data?.data || res.data?.items || res.data || [];
        setCompanies(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Seed selected jurisdiction from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const j = params.get('jurisdiction');
    if (j) setSelectedJurisdiction(j);
  }, [location.search]);

  // Seed selected company from query param (and infer jurisdiction if needed)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyId = params.get('companyId');
    if (!companyId || !companies.length) return;
    const c = companies.find((x) => (x.id || x._id) === companyId);
    if (!c) return;
    if (!selectedJurisdiction) setSelectedJurisdiction(c.jurisdiction || 'Unknown');
    // Only load if not already selected
    if (!selectedCompany || (selectedCompany.id || selectedCompany._id) !== (c.id || c._id)) {
      // Trigger selection and graph load
      handleSelectCompany(c);
    }
  }, [location.search, companies, selectedJurisdiction, selectedCompany]);

  const jurisdictions = useMemo(() => {
    const map = new Map<string, number>();
    companies.forEach((c) => {
      const j = c.jurisdiction || 'Unknown';
      map.set(j, (map.get(j) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([jurisdiction, count]) => ({ jurisdiction, count }))
      .sort((a, b) => b.count - a.count);
  }, [companies]);

  const companiesInSelected = useMemo(() => {
    if (!selectedJurisdiction) return [];
    const list = companies
      .filter((c) => (c.jurisdiction || 'Unknown') === selectedJurisdiction)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [companies, selectedJurisdiction, searchTerm]);

  const companiesSorted = useMemo(() => {
    const arr = [...companiesInSelected];
    if (sortBy === 'owners') {
      return arr.sort((a, b) => {
        const aid = a.id || a._id;
        const bid = b.id || b._id;
        const ahas = !!ownersByCompanyId[aid];
        const bhas = !!ownersByCompanyId[bid];
        return Number(bhas) - Number(ahas) || String(a.name || '').localeCompare(String(b.name || ''));
      });
    }
    if (sortBy === 'missing') {
      const missingScore = (c: any) => {
        let s = 0;
        if (!c.registrationNumber) s += 2;
        if (!c.incorporationDate) s += 1;
        const id = c.id || c._id;
        if (!ownersByCompanyId[id]) s += 2;
        return s;
      };
      return arr.sort((a, b) => missingScore(b) - missingScore(a) || String(a.name || '').localeCompare(String(b.name || '')));
    }
    return arr;
  }, [companiesInSelected, sortBy, ownersByCompanyId]);

  // Prefetch ownership summaries for visible list (capped)
  useEffect(() => {
    if (!selectedJurisdiction) return;
    const toFetch = companiesInSelected.slice(0, 50); // cap to avoid flooding
    toFetch.forEach(async (c) => {
      const id = c.id || c._id;
      if (!id || ownersByCompanyId[id] || ownersLoading[id]) return;
      setOwnersLoading((prev) => ({ ...prev, [id]: true }));
      try {
        const g = await companiesAPI.getCompanyOwnershipGraph(id);
        const graph = g.data;
        const incoming = (graph?.edges || []).filter((e: any) => e.target === id);
        const nodeById: Record<string, any> = {};
        (graph?.nodes || []).forEach((n: any) => (nodeById[n.id] = n));
        const owners = incoming.map((e: any) => {
          const sourceId = e.source;
          const node = nodeById[sourceId] || nodeById[sourceId?.replace('person-', '')];
          const name = node?.label || '—';
          const label = e?.label ? String(e.label) : '';
          const shares = e?.data?.sharesHeld;
          const suffix = label?.endsWith('%')
            ? label
            : shares
            ? `${Number(shares).toLocaleString()} shares`
            : '';
          return suffix ? `${name} (${suffix})` : name;
        });
        const summary = owners.slice(0, 3).join(', ') + (owners.length > 3 ? `, +${owners.length - 3} more` : '');
        setOwnersByCompanyId((prev) => ({ ...prev, [id]: summary }));
      } catch (_) {
        setOwnersByCompanyId((prev) => ({ ...prev, [id]: '' }));
      } finally {
        setOwnersLoading((prev) => ({ ...prev, [id]: false }));
      }
    });
  }, [selectedJurisdiction, companiesInSelected, ownersByCompanyId, ownersLoading]);

  const handleSelectCompany = async (c: any) => {
    setSelectedCompany(c);
    const g = await companiesAPI.getCompanyOwnershipGraph(c.id || c._id);
    setOwnershipGraph(g.data);
    try {
      const dRes = await companiesAPI.getDirectors(String(c.id || c._id));
      const d = Array.isArray(dRes.data) ? dRes.data : [];
      setDirectorsForSelected(d.map((x: any) => ({ personId: x.personId, name: x.name, roleTitle: x.roleTitle, isNominee: x.isNominee, profilePhotoUrl: x.profilePhotoUrl })));
    } catch (_) {
      setDirectorsForSelected([]);
    }
  };

  const openEdit = (c: any) => {
    setEditingCompany(c);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    // Refresh companies list after save
    try {
      const res = await companiesAPI.getAll({ page: 1, limit: 5000 });
      const data = res.data?.data || res.data?.items || res.data || [];
      setCompanies(data);

      // Also refresh selected company if it's the one being edited
      if (selectedCompany && editingCompany && (selectedCompany.id || selectedCompany._id) === (editingCompany.id || editingCompany._id)) {
        const updated = data.find((c: any) => (c.id || c._id) === (editingCompany.id || editingCompany._id));
        if (updated) {
          setSelectedCompany(updated);
        }
      }
    } catch (error) {
      console.error('Failed to reload companies:', error);
    }
  };

  const deleteCompany = async (c: any) => {
    const cid = c.id || c._id;
    if (!cid) return;
    if (!window.confirm('Delete this company? This action cannot be undone.')) return;
    try {
      await companiesAPI.remove(cid);
      toast.success('Company deleted');
      // Remove from local state
      setCompanies((prev) => prev.filter((x) => (x.id || x._id) !== cid));
      if (selectedCompany && (selectedCompany.id || selectedCompany._id) === cid) {
        setSelectedCompany(null);
        setOwnershipGraph(null);
      }
    } catch (e: any) {
      toast.error('Failed to delete company');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const openEnhance = async (c: any) => {
    try {
      setEnhanceOpen(true);
      setEnhanceLoading(true);
      const cid = c.id || c._id;
      const res = await companiesAPI.startEnrichment(String(cid));
      const jobId = res.data?.jobId;
      setEnhanceJobId(jobId);
      const started = Date.now();
      const poll = async () => {
        if (!jobId) return;
        const job = await companiesAPI.getEnrichmentJob(jobId);
        if (job.data?.status === 'complete') {
          setEnhanceData(job.data);
          setEnhanceLoading(false);
          return;
        }
        if (Date.now() - started > 15000) {
          setEnhanceLoading(false);
          toast.error('Enrichment timed out');
          return;
        }
        setTimeout(poll, 600);
      };
      poll();
    } catch (e: any) {
      setEnhanceLoading(false);
      toast.error('Failed to start enrichment');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const toggleFieldApply = (field: string, newValue: any) => {
    setApplySelection((prev) => {
      const next = { ...prev } as Record<string, any>;
      if (next[field] !== undefined) {
        delete next[field];
      } else {
        next[field] = newValue;
      }
      return next;
    });
  };

  const applyEnhancement = async () => {
    if (!enhanceJobId) return;
    try {
      const selectedFields = Object.entries(applySelection).map(([field, newValue]) => ({ field, newValue }));
      await companiesAPI.applyEnrichment(enhanceJobId, selectedFields as any);
      toast.success('Applied suggestions');
      setEnhanceOpen(false);
      setApplySelection({});
      // Refresh selectedCompany and list
      if (selectedCompany?.id) {
        const updated = await companiesAPI.getById(String(selectedCompany.id));
        setSelectedCompany(updated.data);
      }
      const res = await companiesAPI.getAll({ page: 1, limit: 5000 });
      const data = res.data?.data || res.data?.items || res.data || [];
      setCompanies(data);
    } catch (e: any) {
      toast.error('Failed to apply suggestions');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (enhanceData && Array.isArray(enhanceData.candidates)) {
      const pre: Record<string, any> = {};
      const fieldBest: Record<string, { conf: number; val: any }> = {};
      enhanceData.candidates.forEach((cand: any) => {
        const conf = typeof cand.confidence === 'number' ? cand.confidence : 0;
        (cand.suggestedFields || []).forEach((sf: any) => {
          if (!sf || typeof sf.field !== 'string') return;
          const prev = fieldBest[sf.field];
          if (!prev || conf > prev.conf) {
            fieldBest[sf.field] = { conf, val: sf.newValue };
          }
        });
      });
      Object.entries(fieldBest).forEach(([field, best]) => {
        if ((best.conf || 0) >= 0.9) pre[field] = best.val;
      });
      setApplySelection(pre);
    }
  }, [enhanceData]);

  const parentCompanies = useMemo(() => {
    if (!ownershipGraph) return [];
    const incoming = ownershipGraph.edges?.filter((e: any) => e.target === (selectedCompany?.id || selectedCompany?._id));
    const ids = new Set((incoming || []).map((e: any) => e.source));
    const edgeBySource: Record<string, any> = {};
    (incoming || []).forEach((e: any) => {
      edgeBySource[e.source] = e;
    });
    const list = (ownershipGraph.nodes || [])
      .filter((n: any) => ids.has(n.id))
      .map((n: any) => ({ node: n, edge: edgeBySource[n.id] }));
    const score = (item: any) => {
      const lbl = String(item?.edge?.label || '');
      const pct = lbl.endsWith('%') ? parseFloat(lbl) : null;
      const shares = item?.edge?.data?.sharesHeld;
      return pct !== null && !isNaN(pct) ? pct : typeof shares === 'number' ? shares : -1;
    };
    return list.sort((a: any, b: any) => (score(b) as number) - (score(a) as number));
  }, [ownershipGraph, selectedCompany]);

  const subsidiaries = useMemo(() => {
    if (!ownershipGraph) return [];
    const outgoing = ownershipGraph.edges?.filter((e: any) => e.source === (selectedCompany?.id || selectedCompany?._id));
    const ids = new Set(outgoing.map((e: any) => e.target));
    return ownershipGraph.nodes?.filter((n: any) => ids.has(n.id));
  }, [ownershipGraph, selectedCompany]);

  // Ownership breakdown (infographic) for selectedCompany
  const ownershipBreakdown = useMemo((): Array<{ name: string; pct: number; color: string; shares?: number }> => {
    if (!ownershipGraph || !selectedCompany) return [] as Array<{ name: string; pct: number; color: string; shares?: number }>;
    const companyId = selectedCompany?.id || selectedCompany?._id;
    const incoming = (ownershipGraph.edges || []).filter((e: any) => e.target === companyId);
    if (!incoming.length) return [];
    const nodeById: Record<string, any> = {};
    (ownershipGraph.nodes || []).forEach((n: any) => (nodeById[n.id] = n));
    const withPct = incoming.map((e: any, idx: number) => {
      const lbl = String(e?.label || '');
      const pct = lbl.endsWith('%') ? parseFloat(lbl) : NaN;
      const shares = typeof e?.data?.sharesHeld === 'number' ? e.data.sharesHeld : undefined;
      const node = nodeById[e.source] || nodeById[e.source?.replace('person-', '')];
      const name = node?.label || '—';
      const palette = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        theme.palette.secondary.main,
      ];
      const color = palette[idx % palette.length];
      return { name, pct: isNaN(pct) ? NaN : pct, shares, color };
    });
    const anyPct = withPct.some((i: { pct: number }) => !isNaN(i.pct));
    if (anyPct) {
      // Normalize to max 100 if needed
      const total = withPct.reduce((s: number, i: { pct: number }) => (isNaN(i.pct) ? s : s + i.pct), 0);
      return withPct.map((i: { pct: number; name: string; color: string; shares?: number }) => ({ ...i, pct: isNaN(i.pct) ? 0 : i.pct * (total > 0 ? 100 / total : 1) }));
    }
    // Derive from shares
    const totalShares = withPct.reduce((s: number, i: { shares?: number }) => s + (typeof i.shares === 'number' ? i.shares : 0), 0);
    if (totalShares <= 0) return [];
    return withPct.map((i: { shares?: number; pct: number; name: string; color: string }) => ({ ...i, pct: typeof i.shares === 'number' ? (i.shares / totalShares) * 100 : 0 }));
  }, [ownershipGraph, selectedCompany, theme.palette]);

  const jurisdictionSummary = useMemo(() => {
    if (!selectedJurisdiction) return null as null | { total: number; withOwners: number; withoutOwners: number };
    const total = companiesInSelected.length;
    let withOwners = 0;
    companiesInSelected.slice(0, 50).forEach((c) => {
      const id = c.id || c._id;
      if (ownersByCompanyId[id]) withOwners += 1;
    });
    const withoutOwners = Math.max(0, total - withOwners);
    return { total, withOwners, withoutOwners };
  }, [selectedJurisdiction, companiesInSelected, ownersByCompanyId]);

  const missingRegNoCount = useMemo(() => companiesInSelected.filter((c) => !c.registrationNumber).length, [companiesInSelected]);
  const missingIncorpCount = useMemo(() => companiesInSelected.filter((c) => !c.incorporationDate).length, [companiesInSelected]);

  return (
    <>
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      {/* Premium Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          mb: 3,
          color: 'white',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Avatar
            sx={{
              width: { xs: 52, sm: 64 },
              height: { xs: 52, sm: 64 },
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              flexShrink: 0,
            }}
          >
            <PublicIcon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>
              Regional Companies
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Browse companies by jurisdiction and explore ownership structures
            </Typography>
          </Box>
          <Tooltip title="Back to Dashboard">
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<HomeIcon />}
              fullWidth={isMobile}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Dashboard
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      <Breadcrumbs
        sx={{
          mb: { xs: 2, sm: 3 },
          '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
          '& .MuiBreadcrumbs-li': { minWidth: 0 },
        }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
        >
          <HomeIcon fontSize="small" />
          Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => { setSelectedJurisdiction(null); setSelectedCompany(null); setOwnershipGraph(null); }}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
        >
          <PublicIcon fontSize="small" />
          Regions
        </Link>
        {selectedJurisdiction && (
          <Link
            underline="hover"
            color="inherit"
            onClick={() => { setSelectedCompany(null); setOwnershipGraph(null); }}
            sx={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
          >
            {getFlagEmoji(selectedJurisdiction)} {selectedJurisdiction}
          </Link>
        )}
        {selectedCompany && (
          <Typography color="text.primary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: { xs: 140, sm: 300 } }}>
            <BusinessIcon fontSize="small" />
            {selectedCompany.name}
          </Typography>
        )}
      </Breadcrumbs>

      {!selectedJurisdiction && (
        <Grid container spacing={3}>
          {loading
            ? Array.from({ length: 6 }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Paper sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="rectangular" height={20} sx={{ mt: 1 }} />
                  </Paper>
                </Grid>
              ))
            : jurisdictions.map((j) => {
                const jurisdictionColor = getJurisdictionColor(j.jurisdiction);
                const lightColor = lightenColor(jurisdictionColor, 0.92);

                return (
                  <Grid item xs={12} sm={6} md={4} key={j.jurisdiction}>
                    <Paper
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: lightColor,
                        borderLeft: `4px solid ${jurisdictionColor}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: lightenColor(jurisdictionColor, 0.85),
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => setSelectedJurisdiction(j.jurisdiction)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: jurisdictionColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="h6" noWrap sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{j.jurisdiction}</Typography>
                        </Box>
                        <Chip
                          label={j.count}
                          sx={{
                            bgcolor: jurisdictionColor,
                            color: '#fff',
                            fontWeight: 'bold',
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
        </Grid>
      )}

      {selectedJurisdiction && !selectedCompany && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, minWidth: 0 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: getJurisdictionColor(selectedJurisdiction),
                flexShrink: 0,
              }}
            />
            <Typography variant="h6" noWrap sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{getFlagEmoji(selectedJurisdiction)} {selectedJurisdiction} — Companies</Typography>
            <Chip
              label={companiesInSelected.length}
              size="small"
              sx={{
                bgcolor: getJurisdictionColor(selectedJurisdiction),
                color: '#fff',
                fontWeight: 'bold',
                flexShrink: 0,
              }}
            />
          </Box>
          {jurisdictionSummary && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: lightenColor(getJurisdictionColor(selectedJurisdiction), 0.92),
                    borderLeft: `3px solid ${getJurisdictionColor(selectedJurisdiction)}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                  <Typography variant="h5">{jurisdictionSummary.total}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.success.main, 0.08), borderLeft: '3px solid', borderLeftColor: 'success.main' }}>
                  <Typography variant="body2" color="text.secondary">With Shareholders</Typography>
                  <Typography variant="h5">{jurisdictionSummary.withOwners}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.warning.main, 0.08), borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
                  <Typography variant="body2" color="text.secondary">Without Shareholders</Typography>
                  <Typography variant="h5">{jurisdictionSummary.withoutOwners}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Data Quality (this jurisdiction)</Typography>
                  <Box sx={{ display: 'flex', width: '100%', height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
                    {/* Missing Reg */}
                    <Box sx={{ width: `${jurisdictionSummary.total ? (missingRegNoCount / jurisdictionSummary.total) * 100 : 0}%`, bgcolor: 'error.main' }} />
                    {/* Missing Incorp */}
                    <Box sx={{ width: `${jurisdictionSummary.total ? (missingIncorpCount / jurisdictionSummary.total) * 100 : 0}%`, bgcolor: 'warning.main' }} />
                    {/* With owners (subset fetched) */}
                    <Box sx={{ width: `${jurisdictionSummary.total ? (jurisdictionSummary.withOwners / jurisdictionSummary.total) * 100 : 0}%`, bgcolor: 'success.main' }} />
                  </Box>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`No Reg: ${missingRegNoCount}`} color="error" />
                    <Chip size="small" label={`No Incorp: ${missingIncorpCount}`} color="warning" />
                    <Chip size="small" label={`Owners known*: ${jurisdictionSummary.withOwners}`} color="success" />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Select size="small" fullWidth value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
                  <MenuItem value="name">Sort: Name (A–Z)</MenuItem>
                  <MenuItem value="owners">Sort: Owners first</MenuItem>
                  <MenuItem value="missing">Sort: Missing data first</MenuItem>
                </Select>
              </Grid>
            </Grid>
          )}
          
          {/* Jurisdiction Ownership Graph - Collapsible */}
          <Accordion defaultExpanded={false} sx={{ mt: 3, mb: 3 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'white',
                },
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTreeIcon />
                {getFlagEmoji(selectedJurisdiction)} {selectedJurisdiction} — Ownership Network
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <OwnershipGraph
                jurisdiction={selectedJurisdiction}
                height="700px"
              />
            </AccordionDetails>
          </Accordion>

          {companiesInSelected.length === 0 ? (
            <Box p={2} textAlign="center" color="text.secondary">No companies match your search.</Box>
          ) : (
            <List>
              {companiesSorted.map((c) => (
              <React.Fragment key={c.id || c._id}>
                <ListItemButton
                    onClick={() => handleSelectCompany(c)}
                    sx={{
                      borderLeft: '4px solid',
                      borderLeftColor: ownersLoading[c.id || c._id]
                        ? 'divider'
                        : ownersByCompanyId[c.id || c._id]
                        ? 'success.main'
                        : 'warning.main',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: { xs: 40, sm: 56 } }}>
                      <BusinessIcon color="action" />
                    </ListItemIcon>
                  <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="body1" component="span" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                          <Link component={RouterLink} to={`/companies/${encodeURIComponent(c.id || c._id)}`} underline="hover" onClick={(e) => e.stopPropagation()}>
                            {c.name}
                          </Link>
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ minWidth: 0 }}>
                          <Box component="span" sx={{ display: 'block', mb: 0.5, overflowWrap: 'anywhere' }}>
                            {ownersByCompanyId[c.id || c._id]
                              ? ownersByCompanyId[c.id || c._id]
                              : ownersLoading[c.id || c._id]
                              ? 'Loading shareholders…'
                              : 'Shareholders: —'}
                          </Box>
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                            <Tooltip title={c.registrationNumber ? 'Registration Number' : 'Missing Registration Number'}>
                              <AssignmentLateIcon fontSize="small" color={c.registrationNumber ? 'disabled' : 'error'} />
                            </Tooltip>
                            <Tooltip title={c.incorporationDate ? 'Incorporation Date' : 'Missing Incorporation Date'}>
                              <EventBusyIcon fontSize="small" color={c.incorporationDate ? 'disabled' : 'warning'} />
                            </Tooltip>
                            <Tooltip title={ownersByCompanyId[c.id || c._id] ? 'Owners available' : 'Owners unknown*'}>
                              <GroupIcon fontSize="small" color={ownersByCompanyId[c.id || c._id] ? 'success' : 'disabled'} />
                            </Tooltip>
                            <Box component="span" sx={{ color: 'text.secondary', overflowWrap: 'anywhere', minWidth: 0, fontSize: '0.875rem' }}>Reg: {c.registrationNumber || '—'} • Status: {c.status || '—'}</Box>
                            <Box component="span" sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Tooltip title="Download PDF Report">
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  icon={<DownloadIcon fontSize="small" />}
                                  label="PDF"
                                  sx={{ minHeight: 40 }}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const cid = c.id || c._id;
                                    try {
                                      const response = await companiesAPI.downloadReport(String(cid));
                                      const blob = new Blob([response.data], { type: 'application/pdf' });
                                      const url = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = `${c.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                      toast.success('Report downloaded successfully');
                                    } catch (e: any) {
                                      toast.error('Failed to download report');
                                      console.error(e);
                                    }
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Chip size="small" variant="outlined" label="Edit" sx={{ minHeight: 40 }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(c); }} />
                              </Tooltip>
                              <Tooltip title="Delete">
                                <Chip size="small" color="error" variant="outlined" label="Delete" sx={{ minHeight: 40 }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteCompany(c); }} />
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      {selectedCompany && ownershipGraph && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ mb: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="h6" sx={{ mb: 0, overflowWrap: 'anywhere', minWidth: 0 }}>
                  {selectedCompany.name} — Details
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                  <Tooltip title="Download comprehensive PDF report">
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={<DownloadIcon />}
                      fullWidth={isMobile}
                      onClick={async () => {
                        const cid = selectedCompany?.id || selectedCompany?._id;
                        if (!cid) return;
                        try {
                          const response = await companiesAPI.downloadReport(String(cid));
                          const blob = new Blob([response.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${selectedCompany?.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          toast.success('Report downloaded successfully');
                        } catch (e: any) {
                          toast.error('Failed to download report');
                          console.error(e);
                        }
                      }}
                    >
                      PDF
                    </Button>
                  </Tooltip>
                  <Button size="small" variant="outlined" fullWidth={isMobile} onClick={() => openEdit(selectedCompany)}>Edit</Button>
                  <Tooltip title="Enhance from public records">
                    <Button size="small" variant="contained" fullWidth={isMobile} onClick={() => openEnhance(selectedCompany)}>Enhance</Button>
                  </Tooltip>
                  <Button size="small" color="error" variant="contained" fullWidth={isMobile} onClick={() => deleteCompany(selectedCompany)}>Delete</Button>
                </Stack>
              </Box>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Jurisdiction:</Typography>
                <JurisdictionBadge jurisdiction={selectedCompany.jurisdiction} size="small" />
              </Box>
              <Box sx={{ mb: 1 }}>Registration No: {selectedCompany.registrationNumber || '—'}</Box>
              <Box sx={{ mb: 1 }}>
                Status: {selectedCompany.status ? (
                  <Chip
                    label={selectedCompany.status}
                    size="small"
                    color={String(selectedCompany.status).toUpperCase() === 'DISSOLVED' ? 'error' : 'success'}
                    sx={{ ml: 1 }}
                  />
                ) : '—'}
              </Box>
              <Box sx={{ mb: 1 }}>Incorporation: {selectedCompany.incorporationDate ? new Date(selectedCompany.incorporationDate).toLocaleDateString() : '—'}</Box>
              <Box sx={{ mb: 1 }}>Auth. Capital: {selectedCompany.authorisedShareCapitalRaw || '—'}</Box>
              <Box sx={{ mb: 1 }}>Address: {selectedCompany.address || '—'}</Box>
              <Box sx={{ mb: 1 }}>Notes: {selectedCompany.comments || selectedCompany.notes || '—'}</Box>

              {ownershipBreakdown.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Ownership Breakdown</Typography>
                  <Box sx={{ display: 'flex', width: '100%', height: 14, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
                    {ownershipBreakdown.map((o: { pct: number; color: string }, idx: number) => (
                      <Box key={idx} sx={{ width: `${Math.max(0, Math.min(100, o.pct))}%`, bgcolor: o.color }} />
                    ))}
                  </Box>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {ownershipBreakdown.map((o: { name: string; pct: number; color: string; shares?: number }, idx: number) => (
                      <Box key={idx} display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 12, height: 12, bgcolor: o.color, borderRadius: 0.5 }} />
                        <Typography variant="body2">
                          {o.name} — {o.pct.toFixed(1)}%
                          {typeof o.shares === 'number' ? ` (${Number(o.shares).toLocaleString()} shares)` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Shareholders / Parent Owners
              </Typography>
              <List>
                {parentCompanies.map((item: any) => {
                  const n = item.node;
                  const e = item.edge || {};
                  const label = e?.label ? String(e.label) : '';
                  const pct = label.endsWith('%') ? label : '';
                  const sharesNumber = typeof e?.data?.sharesHeld === 'number' ? Number(e.data.sharesHeld) : undefined;
                  const sharesText = !pct && label ? label : '';
                  const sharesDisplay = typeof sharesNumber === 'number'
                    ? `Shares: ${sharesNumber.toLocaleString()}`
                    : sharesText
                    ? sharesText
                    : '';
                  const isPerson = String(n.type) === 'person';
                  const personId = isPerson ? String(n.id).replace(/^person-/, '') : '';

                  const secondaryContent = (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {n.type === 'company' ? (
                        <>
                          <Typography variant="caption">Company</Typography>
                          <JurisdictionBadge jurisdiction={n.jurisdiction} size="small" variant="dot" />
                        </>
                      ) : (
                        <Typography variant="caption">Person</Typography>
                      )}
                      {pct && <Typography variant="caption"> • {pct}</Typography>}
                      {sharesDisplay && <Typography variant="caption"> • {sharesDisplay}</Typography>}
                    </Box>
                  );

                  return (
                    <React.Fragment key={n.id}>
                      <ListItemButton
                        component={RouterLink}
                        to={isPerson ? `/persons/${encodeURIComponent(personId)}` : `/companies/${encodeURIComponent(n.id)}`}
                      >
                        <ListItemText primary={n.label} secondary={secondaryContent} />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Directors
              </Typography>
              {directorsForSelected.length === 0 ? (
                <Box color="text.secondary">No directors found.</Box>
              ) : (
                <List>
                  {directorsForSelected.map((d) => {
                    const initials = d.name
                      ?.split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || '?';
                    
                    return (
                      <React.Fragment key={d.personId}>
                        <ListItemButton component={RouterLink} to={`/persons/${encodeURIComponent(d.personId)}`}>
                          <Avatar
                            src={d.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(d.personId) : undefined}
                            sx={{ width: 36, height: 36, mr: 2, bgcolor: 'primary.main', fontSize: '0.875rem' }}
                          >
                            {initials}
                          </Avatar>
                          <ListItemText
                            primary={d.name}
                            secondary={[d.roleTitle || 'Director', d.isNominee ? 'Nominee' : ''].filter(Boolean).join(' • ')}
                          />
                        </ListItemButton>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" gutterBottom>
                Subsidiaries / Investments
              </Typography>
              <List>
                {subsidiaries.map((n: any) => {
                  const isPerson = String(n.type) === 'person';
                  const personId = isPerson ? String(n.id).replace(/^person-/, '') : '';

                  const secondaryContent = (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {n.type === 'company' ? (
                        <>
                          <Typography variant="caption">Company</Typography>
                          <JurisdictionBadge jurisdiction={n.jurisdiction} size="small" variant="dot" />
                        </>
                      ) : (
                        <Typography variant="caption">Person</Typography>
                      )}
                    </Box>
                  );

                  return (
                    <React.Fragment key={n.id}>
                      <ListItemButton
                        component={RouterLink}
                        to={isPerson ? `/persons/${encodeURIComponent(personId)}` : `/companies/${encodeURIComponent(n.id)}`}
                      >
                        <ListItemText primary={n.label} secondary={secondaryContent} />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>

    <CompanyEditDialog
      open={editOpen}
      onClose={() => {
        setEditOpen(false);
        setEditingCompany(null);
      }}
      company={editingCompany}
      onSave={handleEditSave}
      jurisdictions={jurisdictions.map(j => j.jurisdiction)}
      showAdvancedFields={false}
    />

    {/* Enhancement Drawer - Using Box instead of Drawer to avoid z-index issues */}
    {enhanceOpen && (
      <>
        {/* Backdrop */}
        <Box
          onClick={() => setEnhanceOpen(false)}
          sx={{
            position: 'fixed',
            top: { xs: '56px', sm: '64px' },
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          }}
        />
        {/* Drawer Content */}
        <Box
          sx={{
            position: 'fixed',
            top: { xs: '56px', sm: '64px' },
            right: 0,
            left: { xs: 0, sm: 'auto' },
            width: { xs: '100%', sm: 420 },
            height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
            backgroundColor: 'background.paper',
            boxShadow: 24,
            zIndex: 1001,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 2 }, pt: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Enhance Company</Typography>
        {enhanceLoading && <LinearProgress sx={{ mb: 2 }} />}
        {!enhanceLoading && enhanceData && Array.isArray(enhanceData.candidates) && enhanceData.candidates.length > 0 ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {enhanceData.candidates.map((cand: any) => (
              <Paper key={cand.candidateId} sx={{ p: 1.5, mb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" minWidth={0}>
                  <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                    {cand.providerAttribution?.url ? (
                      <Link href={cand.providerAttribution.url} target="_blank" rel="noreferrer">
                        {cand.providerAttribution?.name || 'Provider'}
                      </Link>
                    ) : (
                      cand.providerAttribution?.name || 'Provider'
                    )}
                  </Typography>
                  <Chip size="small" label={`Conf: ${Math.round((cand.confidence || 0) * 100)}%`} sx={{ flexShrink: 0 }} />
                </Box>
                <List dense>
                  {(cand.suggestedFields || []).map((sf: any) => {
                    const preselect = (cand.confidence || 0) >= 0.9;
                    const checked = applySelection[sf.field] !== undefined || preselect;
                    return (
                      <ListItemButton key={sf.field} onClick={() => toggleFieldApply(sf.field, sf.newValue)}>
                        <ListItemText
                          primary={`${sf.field}`}
                          secondary={`Current: ${selectedCompany?.[sf.field] ?? '—'} → Proposed: ${sf.newValue}`}
                        />
                        <Chip size="small" color={checked ? 'success' : 'default'} label={checked ? 'Apply' : 'Skip'} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Paper>
            ))}
          </Box>
        ) : (
          <Box color="text.secondary" sx={{ mt: 2 }}>No suggestions yet.</Box>
        )}
        <Box sx={{ mt: 'auto' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button onClick={() => setEnhanceOpen(false)} fullWidth={isMobile}>Close</Button>
            <Button variant="contained" disabled={!enhanceData || Object.keys(applySelection).length === 0} onClick={applyEnhancement} fullWidth={isMobile}>Apply selected</Button>
          </Stack>
        </Box>
      </Box>
    </Box>
      </>
    )}
    </>
  );
};

export default Regions;
